import { computed, reactive, ref } from "vue";
import { logger } from "@/infrastructure/services/LoggerService";
import { resolveApiErrorKey } from "@/infrastructure/services/ApiErrorService";
import { createDefaultRfmSettings, RFM_ANALYSIS_PERIOD_MAX, RFM_ANALYSIS_PERIOD_MIN, RFM_MONETARY_THRESHOLD_MAX, } from "../domain/entities/rfmSettings";
import { getRfmSettings, updateRfmSettings } from "../infrastructure/adapters/rfmSettingsService";
const ERR = "workspace.settings.rfm.errors";
/** Backend validator codes → i18n keys (keep in sync with OPDON `RfmSettingsErrorCodes`). */
export const RFM_ERROR_KEYS = {
    "RfmSettings.AnalysisPeriodMonths.OutOfRange": `${ERR}.analysisPeriod`,
    "RfmSettings.Weight.OutOfRange": `${ERR}.weightRange`,
    "RfmSettings.Weights.SumMustBe100": `${ERR}.weightsSum`,
    "RfmSettings.RecencyThresholds.Invalid": `${ERR}.recencyThresholds`,
    "RfmSettings.FrequencyThresholds.Invalid": `${ERR}.frequencyThresholds`,
    "RfmSettings.MonetaryThresholds.Invalid": `${ERR}.monetaryThresholds`,
    "RfmSettings.Currency.Invalid": `${ERR}.currency`,
    // Emitted by OPDON's DuplicateEntityExceptionHandler when two tabs save the singleton row at once.
    DuplicateEntity: `${ERR}.concurrentSave`,
};
function toForm(settings) {
    const dim = (d) => ({
        thresholds: d.thresholds.map(String),
        weight: String(d.weight),
    });
    return {
        analysisPeriodMonths: String(settings.analysisPeriodMonths),
        recency: dim(settings.recency),
        frequency: dim(settings.frequency),
        monetary: { ...dim(settings.monetary), currency: settings.monetary.currency },
        updatedAt: settings.updatedAt,
    };
}
function parseNumber(value) {
    if (value.trim() === "")
        return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}
function parseThresholds(values) {
    const parsed = values.map(parseNumber);
    return parsed.every((v) => v !== null) ? parsed : null;
}
function allIntegers(values) {
    return values.every((v) => Number.isInteger(v));
}
function strictlyIncreasingPositive(values) {
    return values.length === 4 && values[0] > 0 && values.every((v, i) => i === 0 || v > values[i - 1]);
}
// The OPDON column is numeric(10,2): a larger amount is rejected by Postgres and a third
// decimal is silently rounded, so the saved row would no longer match what was typed.
function fitsMonetaryColumn(values) {
    return values.every((v) => v <= RFM_MONETARY_THRESHOLD_MAX && Math.round(v * 100) / 100 === v);
}
export function useRfmSettingsForm() {
    const state = reactive(toForm(createDefaultRfmSettings()));
    let snapshot = JSON.stringify(state);
    const errors = ref({});
    const isLoading = ref(false);
    const isSaving = ref(false);
    const loadError = ref(false);
    const hasLoaded = ref(false);
    const saveErrorKey = ref(null);
    const isDirty = computed(() => JSON.stringify(state) !== snapshot);
    const weightsTotal = computed(() => (parseNumber(state.recency.weight) ?? 0) +
        (parseNumber(state.frequency.weight) ?? 0) +
        (parseNumber(state.monetary.weight) ?? 0));
    function toPayload() {
        const next = {};
        const period = parseNumber(state.analysisPeriodMonths);
        if (period === null)
            next.analysisPeriodMonths = `${ERR}.fieldRequired`;
        // A month count, a donation count and a weight are whole numbers on the wire (int32 in OPDON):
        // a decimal would be rejected by model binding with an opaque 400.
        else if (!Number.isInteger(period))
            next.analysisPeriodMonths = `${ERR}.integerRequired`;
        else if (period < RFM_ANALYSIS_PERIOD_MIN || period > RFM_ANALYSIS_PERIOD_MAX)
            next.analysisPeriodMonths = `${ERR}.analysisPeriod`;
        const dims = {
            recency: parseThresholds(state.recency.thresholds),
            frequency: parseThresholds(state.frequency.thresholds),
            monetary: parseThresholds(state.monetary.thresholds),
        };
        for (const d of ["recency", "frequency", "monetary"]) {
            const values = dims[d];
            if (values === null)
                next[d] = `${ERR}.fieldRequired`;
            else if (d !== "monetary" && !allIntegers(values))
                next[d] = `${ERR}.integerRequired`;
            else if (!strictlyIncreasingPositive(values))
                next[d] = `${ERR}.${d}Thresholds`;
            else if (d === "monetary" && !fitsMonetaryColumn(values))
                next[d] = `${ERR}.monetaryThresholds`;
        }
        const weights = {
            recency: parseNumber(state.recency.weight),
            frequency: parseNumber(state.frequency.weight),
            monetary: parseNumber(state.monetary.weight),
        };
        const weightValues = Object.values(weights);
        if (weightValues.some((w) => w === null))
            next.weights = `${ERR}.fieldRequired`;
        else if (!allIntegers(weightValues))
            next.weights = `${ERR}.integerRequired`;
        else if (weightValues.some((w) => w < 0 || w > 100))
            next.weights = `${ERR}.weightRange`;
        else if (weightsTotal.value !== 100)
            next.weights = `${ERR}.weightsSum`;
        if (!/^[A-Z]{3}$/.test(state.monetary.currency))
            next.currency = `${ERR}.currency`;
        if (Object.keys(next).length > 0)
            return { payload: null, errors: next };
        return {
            errors: next,
            payload: {
                analysisPeriodMonths: period,
                recency: { thresholds: dims.recency, weight: weights.recency },
                frequency: { thresholds: dims.frequency, weight: weights.frequency },
                monetary: {
                    thresholds: dims.monetary,
                    currency: state.monetary.currency,
                    weight: weights.monetary,
                },
            },
        };
    }
    const validation = computed(() => toPayload());
    const isValid = computed(() => validation.value.payload !== null);
    // Null while invalid: a preview computed on a broken grid would show wrong scores.
    const previewSettings = computed(() => validation.value.payload);
    function applySnapshot(settings) {
        Object.assign(state, toForm(settings));
        snapshot = JSON.stringify(state);
        errors.value = {};
        saveErrorKey.value = null;
    }
    async function load() {
        isLoading.value = true;
        loadError.value = false;
        try {
            applySnapshot(await getRfmSettings());
            hasLoaded.value = true;
        }
        catch (err) {
            logger.error("Failed to load RFM settings", err);
            loadError.value = true;
        }
        finally {
            isLoading.value = false;
        }
    }
    function setField(field, value) {
        if (field === "analysisPeriodMonths")
            state.analysisPeriodMonths = value;
        else if (field === "currency")
            state.monetary.currency = value;
        else if (field.endsWith("Weight"))
            state[field.replace("Weight", "")].weight = value;
        else {
            const match = /^(recency|frequency|monetary)Threshold([0-3])$/.exec(field);
            if (match)
                state[match[1]].thresholds[Number(match[2])] = value;
        }
        // Clear-on-correct: the next save re-validates everything.
        errors.value = {};
        saveErrorKey.value = null;
    }
    function reset() {
        Object.assign(state, JSON.parse(snapshot));
        errors.value = {};
        saveErrorKey.value = null;
    }
    async function save() {
        const { payload, errors: nextErrors } = toPayload();
        errors.value = nextErrors;
        if (!payload)
            return "invalid";
        isSaving.value = true;
        try {
            applySnapshot(await updateRfmSettings(payload));
            return "saved";
        }
        catch (err) {
            saveErrorKey.value = resolveApiErrorKey(err, RFM_ERROR_KEYS, `${ERR}.saveFailed`);
            return "failed";
        }
        finally {
            isSaving.value = false;
        }
    }
    return {
        state,
        errors,
        isDirty,
        isLoading,
        isSaving,
        loadError,
        hasLoaded,
        saveErrorKey,
        weightsTotal,
        isValid,
        previewSettings,
        load,
        save,
        reset,
        setField,
    };
}
